import { useState } from 'react'
import { AlertTriangle, Plus } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useNotificationChannels, useNotificationTemplates, useSettingsMutations } from '../api'
import { EmailChannelDialog } from '../components/EmailChannelDialog'
import { TestEmailButton } from '../components/TestEmailButton'

const CHANNELS = ['EMAIL', 'IN_APP', 'SMS', 'PUSH'] as const

/** What this employer says, and on which channels it says it. */
export function NotificationSettingsPage() {
  useBreadcrumb()
  const templates = useNotificationTemplates()
  const channels = useNotificationChannels()
  const actions = useSettingsMutations()

  const [template, setTemplate] = useState<{
    id?: string; code: string; channel: string; locale: string; subject: string; body: string
  } | null>(null)
  const [channel, setChannel] = useState<{
    channel: string; enabled: boolean; fromName: string; fromAddress: string; provider: string
    providerAccount: string; providerSecret: string; note: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editingEmail, setEditingEmail] = useState(false)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Notifications"
        subtitle="What is said, and what actually sends it."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setTemplate({
                code: '', channel: 'EMAIL', locale: 'en', subject: '', body: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New template
          </Button>
        }
      />

      <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
        Email goes through the HUMIS default sender unless you set up your own mail server on the
        email channel below — then it comes from your domain. What else belongs to this company
        is which channels are on and the words themselves. Nothing is seeded: with no template a
        notification falls back to the plain message the product passes, which is honest and
        unbranded.
      </p>

      <Card>
        <h3 className="border-b border-[var(--color-border)] p-3 text-sm font-semibold text-[var(--color-heading)]">
          Channels
        </h3>
        {channels.isPending && <Skeleton className="m-4 h-20" />}
        {channels.isError && (
          <ErrorState error={channels.error} onRetry={() => void channels.refetch()} />
        )}
        <ul className="divide-y divide-[var(--color-border)]">
          {CHANNELS.map((name) => {
            const configured = channels.data?.find((row) => row.channel === name)
            return (
              <li key={name} className="flex flex-wrap items-start justify-between gap-3 p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--color-heading)]">
                    {name.replace('_', '-').toLowerCase()}
                    {configured?.fromAddress && (
                      <span className="ms-2 text-xs font-normal text-[var(--color-muted)]">
                        from {configured.fromAddress}
                      </span>
                    )}
                  </p>
                  {configured?.deliveryNote && configured.usingOwnSmtp && (
                    <p className="text-xs text-[var(--color-muted)]">{configured.deliveryNote}</p>
                  )}
                  {name === 'EMAIL' && !configured && (
                    <p className="text-xs text-[var(--color-muted)]">
                      Using the HUMIS default sender.
                    </p>
                  )}
                  {name === 'EMAIL' && (
                    <div className="mt-2"><TestEmailButton /></div>
                  )}
                  {configured?.deliveryNote && !configured.usingOwnSmtp && (
                    <p className="flex items-start gap-1 text-xs text-amber-800">
                      <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                      {configured.deliveryNote}
                    </p>
                  )}
                  {configured?.hasSecret && name !== 'EMAIL' && (
                    <p className="text-xs text-[var(--color-muted)]">
                      A provider secret is stored, encrypted. It is never read back to this
                      screen.
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <StatusBadge
                    status={configured?.enabled ? 'ON' : 'OFF'}
                    tone={configured?.enabled ? 'success' : 'neutral'}
                    label={configured?.enabled ? 'On' : 'Off'}
                  />
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => {
                      setError(null)
                      if (name === 'EMAIL') {
                        setEditingEmail(true)
                        return
                      }
                      setChannel({
                        channel: name,
                        enabled: configured?.enabled ?? false,
                        fromName: configured?.fromName ?? '',
                        fromAddress: configured?.fromAddress ?? '',
                        provider: configured?.provider ?? '',
                        providerAccount: configured?.providerAccount ?? '',
                        providerSecret: '',
                        note: configured?.note ?? '',
                      })
                    }}
                  >
                    Configure
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      </Card>

      <Card>
        <h3 className="border-b border-[var(--color-border)] p-3 text-sm font-semibold text-[var(--color-heading)]">
          Templates
        </h3>
        {templates.isPending && <Skeleton className="m-4 h-16" />}
        {templates.isSuccess && templates.data.length === 0 && (
          <EmptyState
            title="No template"
            description="Nothing is seeded. Until you write one, notifications go out in the product's own plain words rather than yours."
          />
        )}
        {templates.isSuccess && templates.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
              <tr>
                <th className="p-3 text-start font-medium">Event</th>
                <th className="p-3 text-start font-medium">Channel</th>
                <th className="p-3 text-start font-medium">Language</th>
                <th className="p-3 text-start font-medium">Subject</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {templates.data.map((row) => (
                <tr key={row.id} className="border-b border-[var(--color-border)]">
                  <td className="p-3 font-mono text-xs">{row.code}</td>
                  <td className="p-3">{row.channel.toLowerCase()}</td>
                  <td className="p-3">{row.locale}</td>
                  <td className="p-3">{row.subject ?? '—'}</td>
                  <td className="p-3 text-end">
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => {
                        setError(null)
                        setTemplate({
                          id: row.id, code: row.code, channel: row.channel, locale: row.locale,
                          subject: row.subject ?? '', body: row.body,
                        })
                      }}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <FormDialog
        open={template != null}
        onOpenChange={(open) => { if (!open) { setTemplate(null); setError(null) } }}
        title={template?.id ? 'Edit template' : 'New template'}
        submitLabel="Save"
        loading={actions.saveTemplate.isPending}
        error={error}
        onSubmit={() => {
          if (!template) return
          void (async () => {
            setError(null)
            try {
              await actions.saveTemplate.mutateAsync({
                id: template.id,
                code: template.code,
                channel: template.channel,
                locale: template.locale,
                subject: template.subject || null,
                body: template.body,
                active: true,
              })
              setTemplate(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That template could not be saved')
            }
          })()
        }}
      >
        {template && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="template-code">Event</Label>
                <Input
                  id="template-code" value={template.code} placeholder="leave.approved"
                  onChange={(event) => setTemplate({ ...template, code: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="template-channel">Channel</Label>
                <Select
                  id="template-channel" className="w-full" value={template.channel}
                  onChange={(event) => setTemplate({ ...template, channel: event.target.value })}
                >
                  {CHANNELS.map((name) => (
                    <option key={name} value={name}>{name.toLowerCase()}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="template-locale">Language</Label>
                <Input
                  id="template-locale" value={template.locale}
                  onChange={(event) => setTemplate({ ...template, locale: event.target.value })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="template-subject">Subject</Label>
              <Input
                id="template-subject" value={template.subject}
                onChange={(event) => setTemplate({ ...template, subject: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Required for email. One with an empty subject line is the one that gets filtered.
              </p>
            </div>
            <div>
              <Label htmlFor="template-body">Message</Label>
              <Textarea
                id="template-body" rows={6} value={template.body}
                onChange={(event) => setTemplate({ ...template, body: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <EmailChannelDialog
        // Remounted on every open, so the form starts from what is saved now.
        key={editingEmail ? 'open' : 'closed'}
        row={channels.data?.find((row) => row.channel === 'EMAIL')}
        open={editingEmail}
        onOpenChange={setEditingEmail}
      />

      <FormDialog
        open={channel != null}
        onOpenChange={(open) => { if (!open) { setChannel(null); setError(null) } }}
        title={channel ? `${channel.channel.toLowerCase()} channel` : ''}
        submitLabel="Save"
        loading={actions.saveChannel.isPending}
        error={error}
        onSubmit={() => {
          if (!channel) return
          void (async () => {
            setError(null)
            try {
              await actions.saveChannel.mutateAsync({
                channel: channel.channel,
                enabled: channel.enabled,
                fromName: channel.fromName || null,
                fromAddress: channel.fromAddress || null,
                provider: channel.provider || null,
                providerAccount: channel.providerAccount || null,
                providerSecret: channel.providerSecret || null,
                note: channel.note || null,
              })
              setChannel(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That channel could not be saved')
            }
          })()
        }}
      >
        {channel && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox" className="size-4" checked={channel.enabled}
                onChange={(event) => setChannel({ ...channel, enabled: event.target.checked })}
              />
              Send on this channel
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="channel-from-name">From name</Label>
                <Input
                  id="channel-from-name" value={channel.fromName}
                  onChange={(event) => setChannel({ ...channel, fromName: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="channel-from-address">From address</Label>
                <Input
                  id="channel-from-address" value={channel.fromAddress}
                  onChange={(event) => setChannel({ ...channel, fromAddress: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="channel-provider">Provider</Label>
                <Input
                  id="channel-provider" value={channel.provider}
                  onChange={(event) => setChannel({ ...channel, provider: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="channel-account">Provider account</Label>
                <Input
                  id="channel-account" value={channel.providerAccount}
                  onChange={(event) =>
                    setChannel({ ...channel, providerAccount: event.target.value })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="channel-secret">Provider secret</Label>
              <Input
                id="channel-secret" type="password" value={channel.providerSecret}
                placeholder="Leave empty to keep what is stored"
                onChange={(event) =>
                  setChannel({ ...channel, providerSecret: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Encrypted at rest and never read back to this screen. Leaving it empty keeps
                whatever is stored.
              </p>
            </div>
            <div>
              <Label htmlFor="channel-note">Note</Label>
              <Textarea
                id="channel-note" value={channel.note}
                onChange={(event) => setChannel({ ...channel, note: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
